import Button from '@codegouvfr/react-dsfr/Button';
import type { createModal } from '@codegouvfr/react-dsfr/Modal';
import { useIsModalOpen } from '@codegouvfr/react-dsfr/Modal/useIsModalOpen';
import {
  Department,
  DepartmentLabels
} from 'maestro-shared/referential/Department';
import { Region, RegionList, Regions } from 'maestro-shared/referential/Region';
import { StageLabels, StageList } from 'maestro-shared/referential/Stage';
import type { Company } from 'maestro-shared/schema/Company/Company';
import type { ProgrammingPlanChecked } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlans';
import {
  isPPVSubPlan,
  subPlansForStages
} from 'maestro-shared/schema/ProgrammingPlan/ProgrammingSubPlan';
import {
  companiesIsRequired,
  departmentIsRequired,
  laboratoryIsRequired,
  regionIsRequired,
  stagesIsRequired,
  type UserListItem,
  UserToCreateRefined
} from 'maestro-shared/schema/User/User';
import { managerStages } from 'maestro-shared/schema/User/UserManagement';
import { UserRoleLabels } from 'maestro-shared/schema/User/UserRole';
import type { Nullable } from 'maestro-shared/utils/typescript';
import type React from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { assert, type Equals } from 'tsafe';
import AppServiceErrorAlert from '../../../components/_app/AppErrorAlert/AppServiceErrorAlert';
import { AppMultiSelect } from '../../../components/_app/AppMultiSelect/AppMultiSelect';
import AppRequiredInput from '../../../components/_app/AppRequired/AppRequiredInput';
import AppSelect from '../../../components/_app/AppSelect/AppSelect';
import { selectOptionsFromList } from '../../../components/_app/AppSelect/AppSelectOption';
import AppTextInput from '../../../components/_app/AppTextInput/AppTextInput';
import CompanySearch from '../../../components/CompanySearch/CompanySearch';
import { useAuthentication } from '../../../hooks/useAuthentication';
import { useForm } from '../../../hooks/useForm';
import { ApiClientContext } from '../../../services/apiClient';
import { useUserManagement } from '../useUserManagement';

interface Props {
  userToUpdate: null | UserListItem;
  modal: ReturnType<typeof createModal>;
  setAlertMessage: (message: string) => void;
  programmingPlans: ProgrammingPlanChecked[];
}

const regionOptions = selectOptionsFromList(RegionList, {
  labels: RegionList.reduce(
    (acc, r) => {
      acc[r] = Regions[r].name;
      return acc;
    },
    {} as Record<string, string>
  ),
  withSort: true
});

const userDefaultValue: Nullable<UserToCreateRefined> = {
  email: null,
  roles: [],
  stages: [],
  region: null,
  department: null,
  companies: [],
  laboratoryId: null,
  disabled: false
};

export const UserModal = ({
  userToUpdate,
  modal,
  setAlertMessage,
  programmingPlans,
  ..._rest
}: Props) => {
  assert<Equals<keyof typeof _rest, never>>();

  const apiClient = useContext(ApiClientContext);

  const { account } = useAuthentication();
  const { manageableRoles, scope } = useUserManagement();

  const allSubPlans = useMemo(
    () => programmingPlans.flatMap((p) => p.subPlans),
    [programmingPlans]
  );

  const selectableStages = useMemo(() => {
    const managedStages = account ? managerStages(account) : [];

    return managedStages === null
      ? StageList
      : StageList.filter((stage) => managedStages.includes(stage));
  }, [account]);

  // Les stades de la cible hors du périmètre du gestionnaire restent affichés mais ne
  // sont pas modifiables
  const lockedStages = useMemo(
    () =>
      (userToUpdate?.stages ?? []).filter(
        (stage) => !selectableStages.includes(stage)
      ),
    [userToUpdate, selectableStages]
  );

  const [findCompanies] = apiClient.useLazyFindCompaniesQuery();

  const [createUser, createUserResult] = apiClient.useCreateUserMutation();
  const [updateUser, updateUserResult] = apiClient.useUpdateUserMutation();

  const [user, setUser] =
    useState<Nullable<UserToCreateRefined>>(userDefaultValue);

  const programmingSubPlans = useMemo(
    () => subPlansForStages(allSubPlans, user.stages ?? []),
    [allSubPlans, user.stages]
  );

  const showStages = useMemo(
    () => stagesIsRequired(user) && selectableStages.length > 1,
    [user.roles, selectableStages]
  );

  const forcedRegion = scope === 'national' ? null : (account?.region ?? null);
  const forcedDepartment =
    scope === 'departmental' ? (account?.department ?? null) : null;

  const { data: laboratories } = apiClient.useFindLaboratoriesQuery(
    {},
    {
      skip: !laboratoryIsRequired(user)
    }
  );

  const form = useForm(UserToCreateRefined, {
    ...user
  });

  const departmentOptions = useMemo(() => {
    if (!user.region) {
      return [];
    }

    return selectOptionsFromList(Regions[user.region].departments, {
      labels: DepartmentLabels,
      withSort: true
    });
  }, [user.region]);

  useEffect(() => {
    if (userToUpdate) {
      const { id, name, ...rest } = userToUpdate;
      setUser(rest);
    }
  }, [userToUpdate]);

  useEffect(() => {
    if (stagesIsRequired(user) && selectableStages.length === 1) {
      setUser((u) => ({
        ...u,
        stages: [...lockedStages, ...selectableStages]
      }));
    }
  }, [user.roles, selectableStages, lockedStages]);

  useEffect(() => {
    if (!regionIsRequired(user)) {
      setUser((u) => ({ ...u, region: forcedRegion }));
    }
    if (!departmentIsRequired(user)) {
      setUser((u) => ({ ...u, department: forcedDepartment }));
    }
  }, [user.roles, user.stages, user.region, forcedRegion, forcedDepartment]);

  const [companies, setCompanies] = useState<Company[]>([]);
  useEffect(() => {
    if (
      companiesIsRequired({ stages: user.stages, roles: user.roles }) &&
      !programmingSubPlans.some((_) => isPPVSubPlan(_))
    ) {
      findCompanies({
        kinds: ['MEAT_SLAUGHTERHOUSE', 'POULTRY_SLAUGHTERHOUSE'],
        region: user.region ?? undefined,
        department: user.department ?? undefined
      })
        .unwrap()
        .then((data) => {
          setCompanies(data);
        });
    }
  }, [
    user.region,
    user.department,
    programmingSubPlans,
    user.roles,
    findCompanies
  ]);

  useIsModalOpen(modal, {
    onConceal: () => {
      form.reset();
      createUserResult.reset();
      updateUserResult.reset();
      setTimeout(() => {
        setUser(userDefaultValue);
      }, 2);
    }
  });

  const submit = async (e: React.MouseEvent<HTMLElement>) => {
    await form.validate(async (n) => {
      try {
        if (userToUpdate?.id) {
          await updateUser({
            ...n,
            userId: userToUpdate.id
          }).unwrap();
          setAlertMessage(
            `L'utilisateur ${userToUpdate.name} a bien été modifié.`
          );
        } else {
          await createUser(n).unwrap();
          setAlertMessage(`L'utilisateur ${n.email} a bien été créé.`);
        }
        e.preventDefault();
        modal.close();
      } catch (_e: any) {
        /* empty */
      }
    });
  };

  return (
    <modal.Component
      title={
        !userToUpdate?.id ? 'Nouvel utilisateur' : "Modification d'utilisateur"
      }
      concealingBackdrop={false}
      topAnchor
      buttons={[
        {
          children: 'Annuler',
          doClosesModal: true,
          priority: 'secondary'
        },
        {
          children: 'Enregistrer',
          onClick: submit,
          doClosesModal: false,
          priority: 'primary'
        }
      ]}
    >
      <form>
        <AppTextInput
          onChange={(e) => setUser((u) => ({ ...u, email: e.target.value }))}
          inputForm={form}
          inputKey="email"
          label="Courriel"
          type={'email'}
          value={user.email ?? ''}
          required
        />
        <AppMultiSelect
          inputForm={form}
          inputKey={'roles'}
          items={manageableRoles}
          onChange={(roles) =>
            setUser((u) => ({
              ...u,
              roles
            }))
          }
          values={user.roles ?? []}
          keysWithLabels={UserRoleLabels}
          defaultLabel={'rôle sélectionné'}
          label={'Rôles'}
          nativeSelectProps={{
            'data-testid': 'user-form-role-select'
          }}
          required
        />
        {!forcedRegion && regionIsRequired(user) && (
          <AppSelect
            onChange={(e) => {
              const { data, success } = Region.safeParse(e.target.value);
              if (success) {
                setUser((u) => ({ ...u, region: data, department: null }));
              }
            }}
            value={user.region ?? ''}
            inputForm={form}
            inputKey={'region'}
            label="Région"
            options={regionOptions}
            required
          />
        )}
        {!forcedDepartment &&
          user.roles &&
          user.region &&
          departmentIsRequired(user) && (
            <AppSelect
              onChange={(e) => {
                const { data, success } = Department.safeParse(e.target.value);
                setUser((u) => ({
                  ...u,
                  department: success ? data : null
                }));
              }}
              value={user.department ?? ''}
              inputForm={form}
              inputKey={'department'}
              label="Département"
              options={departmentOptions}
              required
            />
          )}
        {showStages && (
          <AppMultiSelect
            inputForm={form}
            inputKey={'stages'}
            items={selectableStages}
            onChange={(v) =>
              setUser((u) => ({
                ...u,
                stages: [
                  ...lockedStages,
                  ...v.filter((stage) => selectableStages.includes(stage))
                ]
              }))
            }
            values={user.stages ?? []}
            keysWithLabels={StageLabels}
            defaultLabel={'stade sélectionné'}
            label={
              <>
                Stades de prélèvement
                {selectableStages.some(
                  (stage) => !user.stages?.includes(stage)
                ) && (
                  <Button
                    priority="tertiary no outline"
                    size="small"
                    onClick={() =>
                      setUser((u) => ({
                        ...u,
                        stages: [...lockedStages, ...selectableStages]
                      }))
                    }
                  >
                    Tout sélectionner
                  </Button>
                )}
                {selectableStages.some((stage) =>
                  user.stages?.includes(stage)
                ) && (
                  <Button
                    priority="tertiary no outline"
                    size="small"
                    onClick={() =>
                      setUser((u) => ({
                        ...u,
                        stages: lockedStages
                      }))
                    }
                  >
                    Tout désélectionner
                  </Button>
                )}
              </>
            }
            required={stagesIsRequired(user)}
          />
        )}

        {companiesIsRequired({ stages: user.stages, roles: user.roles }) && (
          <CompanySearch
            label={
              <>
                Abattoirs
                <AppRequiredInput />
                {companies.length > 0 &&
                  companies.some(
                    (c) => !user.companies?.some((uc) => uc.siret === c.siret)
                  ) && (
                    <Button
                      priority="tertiary no outline"
                      size="small"
                      onClick={() =>
                        setUser((u) => ({
                          ...u,
                          companies: [
                            ...(u.companies ?? []),
                            ...companies.filter(
                              (c) =>
                                !u.companies?.some((uc) => uc.siret === c.siret)
                            )
                          ]
                        }))
                      }
                    >
                      Tout sélectionner
                    </Button>
                  )}
                {(user.companies?.length ?? 0) > 0 && (
                  <Button
                    priority="tertiary no outline"
                    size="small"
                    onClick={() => setUser((u) => ({ ...u, companies: [] }))}
                  >
                    Tout désélectionner
                  </Button>
                )}
              </>
            }
            multi={true}
            initialValue={user.companies ?? []}
            onSelect={(v) => {
              setUser((u) => ({
                ...u,
                companies: v
              }));
            }}
            state={form.messageType('companies')}
            stateRelatedMessage={
              form.message('companies') ?? 'Abattoirs correctement renseignés'
            }
            companies={companies.filter(
              (c) => !user.companies?.some((uc) => uc.siret === c.siret)
            )}
          />
        )}
        {laboratoryIsRequired(user) && (
          <AppSelect
            onChange={(e) => {
              setUser((u) => ({ ...u, laboratoryId: e.target.value }));
            }}
            value={user.laboratoryId ?? ''}
            inputForm={form}
            inputKey={'laboratoryId'}
            label="Laboratoire"
            options={selectOptionsFromList(
              laboratories?.map((lab) => lab.id) ?? [],
              {
                labels: laboratories?.reduce(
                  (acc, lab) => {
                    acc[lab.id] = lab.name;
                    return acc;
                  },
                  {} as Record<string, string>
                ),
                withSort: true
              }
            )}
            required
          />
        )}

        <AppServiceErrorAlert call={createUserResult} />
        <AppServiceErrorAlert call={updateUserResult} />
      </form>
    </modal.Component>
  );
};
